/**
 * Bitácora del Sistema — consulta del registro de acciones.
 *
 * Todo lo que el backend registra vía app/auditoria.py: quién hizo qué,
 * cuándo y qué cambió (diff campo a campo en `detalles`). Filtros
 * combinables por rango de fechas, usuario, módulo, acción, sucursal,
 * préstamo, chasis y texto libre; todos menos el texto libre resuelven por
 * índice en el servidor.
 *
 * La tabla pagina en el servidor (la bitácora crece rápido). Clic en una
 * fila abre el detalle con el antes/después y, si el evento referencia un
 * préstamo, el salto directo a su expediente.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useGlobalMDAlert } from "context";
import { FP, PageHeader, fpInputSx, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

const sectionBox = {
  border: `1px solid ${FP.color.border}`,
  borderRadius: 2.5,
  p: { xs: 2, sm: 2.6 },
  mb: 2.4,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
};

const tituloSeccion = {
  fontWeight: 800,
  fontSize: "0.98rem",
  color: FP.color.textPrimary,
  mb: 0.4,
};

const subtituloSeccion = { fontSize: "0.82rem", color: FP.color.textSecondary, mb: 2 };

const hoyISO = () => new Date().toISOString().slice(0, 10);
const hace7DiasISO = () => {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
};

// Color del chip por verbo: verde = entra valor/nace algo, azul = cambia,
// rojo = se deshace, ámbar = calendario/condiciones, gris = neutro.
const ESTILO_ACCION = {
  crear: { bg: FP.color.successSoft, fg: FP.color.successDark },
  cobrar: { bg: FP.color.successSoft, fg: FP.color.successDark },
  modificar: { bg: FP.color.accentBlueSoft, fg: FP.color.accentBlue },
  restaurar: { bg: FP.color.accentBlueSoft, fg: FP.color.accentBlue },
  anular: { bg: FP.color.dangerSoft, fg: FP.color.danger },
  eliminar: { bg: FP.color.dangerSoft, fg: FP.color.danger },
  reprogramar: { bg: FP.color.warningSoft, fg: "#B45309" },
  recalcular: { bg: FP.color.warningSoft, fg: "#B45309" },
  imprimir: { bg: FP.color.surfaceAlt, fg: FP.color.textSecondary },
  importar: { bg: FP.color.infoSoft, fg: FP.color.info },
};

function ChipAccion({ accion }) {
  const estilo = ESTILO_ACCION[accion] || {
    bg: FP.color.surfaceAlt,
    fg: FP.color.textSecondary,
  };
  return (
    <Chip
      size="small"
      label={accion}
      sx={{ bgcolor: estilo.bg, color: estilo.fg, fontWeight: 700, textTransform: "capitalize" }}
    />
  );
}

const capitalizar = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// detalles llega como JSON serializado (o null). Cualquier cosa rara → [].
const parseDetalles = (raw) => {
  try {
    const d = JSON.parse(raw);
    return Array.isArray(d) ? d : [];
  } catch (e) {
    return [];
  }
};

const FILTROS_INICIAL = {
  fechadesde: hace7DiasISO(),
  fechahasta: hoyISO(),
  filtro_usuario: "",
  modulo: "",
  accion: "",
  sucursal: "",
  noprest: "",
  chasis: "",
  texto: "",
};

export default function Bitacora() {
  const { showAlert } = useGlobalMDAlert();
  const { post } = useApi();
  const navigate = useNavigate();
  const sucursales = useSucursales({ conTodos: true });

  const [filtros, setFiltros] = useState(FILTROS_INICIAL);
  const [combos, setCombos] = useState({ usuarios: [], modulos: [], acciones: [] });
  const [eventos, setEventos] = useState([]);
  const [total, setTotal] = useState(0);
  const [cargando, setCargando] = useState(false);
  const [paginacion, setPaginacion] = useState({ page: 0, pageSize: 50 });
  const [detalle, setDetalle] = useState(null); // fila seleccionada (dialog)

  const setF = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));

  useEffect(() => {
    let activo = true;
    post("/api/bitacora_filtros", {}).then(({ ok, data }) => {
      if (activo && ok) setCombos(data);
    });
    return () => {
      activo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buscar = useCallback(
    (pagina, pageSize) => {
      setCargando(true);
      // "0-TODOS" del selector de sucursales = sin filtro.
      const sucursal = filtros.sucursal.split("-")[0].trim() === "0" ? "" : filtros.sucursal;
      post("/api/bitacora_listar", {
        ...filtros,
        sucursal,
        pagina,
        limite: pageSize,
      })
        .then(({ ok, data }) => {
          if (ok && Array.isArray(data.eventos)) {
            setEventos(data.eventos);
            setTotal(data.total || 0);
          } else {
            showAlert({
              message: data.Error || "No se pudo consultar la bitacora",
              color: "error",
            });
          }
        })
        .finally(() => setCargando(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtros]
  );

  // Primera carga y cada cambio de página del grid.
  useEffect(() => {
    buscar(paginacion.page, paginacion.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginacion]);

  const aplicarFiltros = () => {
    if (paginacion.page === 0) buscar(0, paginacion.pageSize);
    else setPaginacion((p) => ({ ...p, page: 0 }));
  };

  const limpiar = () => {
    setFiltros(FILTROS_INICIAL);
    setPaginacion((p) => ({ ...p, page: 0 }));
  };

  const columnas = [
    { field: "fecha", headerName: "Fecha", width: 140 },
    { field: "usuario", headerName: "Usuario", width: 110 },
    {
      field: "accion",
      headerName: "Acción",
      width: 120,
      renderCell: (params) => <ChipAccion accion={params.value} />,
    },
    { field: "descripcion", headerName: "Descripción", flex: 1, minWidth: 320 },
    { field: "modulo", headerName: "Módulo", width: 120 },
    { field: "sucursal", headerName: "Suc.", width: 70, align: "center", headerAlign: "center" },
    { field: "noprest", headerName: "Préstamo", width: 90 },
    { field: "chasis", headerName: "Chasis", width: 140 },
  ];

  const detallesFila = detalle ? parseDetalles(detalle.detalles) : [];

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Bitácora del Sistema"
        subtitle="Registro de acciones: quién hizo qué, cuándo y qué cambió"
      />
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={sectionBox}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 0.4 }}>
            <HistoryOutlinedIcon sx={{ fontSize: 20, color: FP.color.navy }} />
            <Typography sx={{ ...tituloSeccion, mb: 0 }}>Filtros</Typography>
          </Stack>
          <Typography sx={subtituloSeccion}>
            Combina los filtros que necesites. Préstamo y chasis encuentran el evento aunque haya
            quedado registrado bajo otra entidad (ej. los recibos de un préstamo).
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                type="date"
                label="Desde"
                value={filtros.fechadesde}
                onChange={setF("fechadesde")}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.5}>
              <TextField
                fullWidth
                type="date"
                label="Hasta"
                value={filtros.fechahasta}
                onChange={setF("fechahasta")}
                InputLabelProps={{ shrink: true }}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.8}>
              <TextField
                select
                fullWidth
                label="Usuario"
                value={filtros.filtro_usuario}
                onChange={setF("filtro_usuario")}
                sx={fpInputSx}
              >
                <MenuItem value="">Todos</MenuItem>
                {(combos.usuarios || []).map((u) => (
                  <MenuItem key={u} value={u}>
                    {u}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.8}>
              <TextField
                select
                fullWidth
                label="Módulo"
                value={filtros.modulo}
                onChange={setF("modulo")}
                sx={fpInputSx}
              >
                <MenuItem value="">Todos</MenuItem>
                {(combos.modulos || []).map((m) => (
                  <MenuItem key={m} value={m}>
                    {capitalizar(m)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.6}>
              <TextField
                select
                fullWidth
                label="Acción"
                value={filtros.accion}
                onChange={setF("accion")}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {(combos.acciones || []).map((a) => (
                  <MenuItem key={a} value={a}>
                    {capitalizar(a)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.8}>
              <TextField
                select
                fullWidth
                label="Sucursal"
                value={filtros.sucursal}
                onChange={setF("sucursal")}
                sx={fpInputSx}
              >
                <MenuItem value="">Todas</MenuItem>
                {sucursales.map((s) => (
                  <MenuItem key={s.label} value={s.label}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={6} sm={3} md={1.4}>
              <TextField
                fullWidth
                label="Préstamo"
                value={filtros.noprest}
                onChange={setF("noprest")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={1.8}>
              <TextField
                fullWidth
                label="Chasis"
                value={filtros.chasis}
                onChange={setF("chasis")}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="Texto en la descripción"
                value={filtros.texto}
                onChange={setF("texto")}
                onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
                sx={fpInputSx}
              />
            </Grid>
            <Grid item xs={12} md="auto">
              <Stack direction="row" spacing={1.2}>
                <Button
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={aplicarFiltros}
                  sx={{
                    bgcolor: FP.color.navy,
                    color: "#fff",
                    fontWeight: 700,
                    px: 3,
                    "&:hover": { bgcolor: FP.color.navySoft },
                  }}
                >
                  Buscar
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RestartAltIcon />}
                  onClick={limpiar}
                  sx={{ color: FP.color.textSecondary, borderColor: FP.color.borderStrong }}
                >
                  Limpiar
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Box sx={sectionBox}>
          <Stack direction="row" alignItems="center" spacing={1.2} sx={{ mb: 1.5 }}>
            <Typography sx={{ ...tituloSeccion, mb: 0 }}>Eventos</Typography>
            <Chip
              size="small"
              label={`${total.toLocaleString("es-DO")} registro${total === 1 ? "" : "s"}`}
              sx={{ bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 700 }}
            />
          </Stack>
          <Box sx={{ bgcolor: "#fff" }}>
            <DataGrid
              autoHeight
              rows={eventos}
              columns={columnas}
              loading={cargando}
              rowHeight={44}
              paginationMode="server"
              rowCount={total}
              pageSizeOptions={[50, 100, 200]}
              paginationModel={paginacion}
              onPaginationModelChange={setPaginacion}
              sx={{ ...fpDataGridSx, "--DataGrid-overlayHeight": "160px", cursor: "pointer" }}
              disableRowSelectionOnClick
              onRowClick={(params) => setDetalle(params.row)}
              localeText={{ noRowsLabel: "No hay eventos registrados con esos filtros" }}
            />
          </Box>
        </Box>
      </Box>

      {/* Detalle del evento */}
      <Dialog open={detalle !== null} onClose={() => setDetalle(null)} maxWidth="md" fullWidth>
        {detalle && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Stack direction="row" alignItems="center" spacing={1.2} flexWrap="wrap" useFlexGap>
                <ChipAccion accion={detalle.accion} />
                <Typography sx={{ fontWeight: 800, fontSize: "1rem" }}>
                  {capitalizar(detalle.entidad)} {detalle.entidad_id}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Typography sx={{ fontSize: "0.85rem", color: FP.color.textSecondary }}>
                  {detalle.fecha} · {detalle.usuario}
                </Typography>
              </Stack>
            </DialogTitle>
            <DialogContent dividers>
              <Typography sx={{ fontSize: "0.95rem", color: FP.color.textPrimary, mb: 2 }}>
                {detalle.descripcion}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
                <Chip size="small" label={`Módulo: ${detalle.modulo}`} variant="outlined" />
                {detalle.sucursal && (
                  <Chip size="small" label={`Sucursal: ${detalle.sucursal}`} variant="outlined" />
                )}
                {detalle.noprest && (
                  <Chip size="small" label={`Préstamo: ${detalle.noprest}`} variant="outlined" />
                )}
                {detalle.chasis && (
                  <Chip size="small" label={`Chasis: ${detalle.chasis}`} variant="outlined" />
                )}
                {detalle.origen && detalle.origen !== "web" && (
                  <Chip
                    size="small"
                    label={`Origen: ${detalle.origen}`}
                    sx={{ bgcolor: FP.color.warningSoft, color: "#B45309", fontWeight: 700 }}
                  />
                )}
              </Stack>
              {detallesFila.length > 0 && (
                <Table size="small" sx={{ border: `1px solid ${FP.color.border}` }}>
                  <TableHead sx={{ display: "table-header-group" }}>
                    <TableRow sx={{ bgcolor: FP.color.surfaceAlt }}>
                      <TableCell sx={{ fontWeight: 800 }}>Campo</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Antes</TableCell>
                      <TableCell sx={{ fontWeight: 800 }}>Después</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detallesFila.map((d, i) => (
                      <TableRow key={`${d.campo}-${i}`}>
                        <TableCell sx={{ fontWeight: 700 }}>{d.campo}</TableCell>
                        <TableCell sx={{ color: FP.color.textSecondary }}>
                          {String(d.antes ?? "") || "—"}
                        </TableCell>
                        <TableCell>{String(d.despues ?? "") || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.6 }}>
              {detalle.noprest && (
                <Button
                  startIcon={<OpenInNewIcon />}
                  onClick={() => navigate(`/prestamos/expediente/${detalle.noprest}`)}
                  sx={{ color: FP.color.accentBlue, fontWeight: 700 }}
                >
                  Ver expediente
                </Button>
              )}
              <Box sx={{ flexGrow: 1 }} />
              <Button onClick={() => setDetalle(null)} sx={{ color: FP.color.textSecondary }}>
                Cerrar
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
